// SurveySettingForm.tsx
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  InputBase,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  getSurveySetting,
  updatedSurveySettingResponse,
  updateSurveySettingAction,
} from "../../../../store/reducers/settingSlice";
import { setNotificationAction } from "../../../../store/reducers/mmymSlice";
import Notification from "../../../../components/common/notification/Notification";
import PageTitle from "../../../../components/pageTitle/PageTitle";

interface SurveySettingFormProps {
  setDirty: (dirty: boolean) => void;
  isDirty: boolean;
}

const generateTimeSlots = (start: string, end: string, interval = 30) => {
  const slots: string[] = [];
  if (!start || !end || end <= start) return slots;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let cur = sh * 60 + sm;
  const endM = eh * 60 + em;
  while (cur <= endM) {
    slots.push(
      `${String(Math.floor(cur / 60)).padStart(2, "0")}:${String(cur % 60).padStart(2, "0")}`,
    );
    cur += interval;
  }
  return slots;
};

const SurveySettingForm: React.FC<SurveySettingFormProps> = ({
  setDirty,
  isDirty,
}) => {
  const dispatch = useDispatch();
  const { surveySettingResponse, isSurveySettingResponse } = useSelector(
    (state: any) => state.setting,
  );

  const [startTime, setStartTime] = useState("07:00");
  const [endTime, setEndTime] = useState("18:00");
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(getSurveySetting());
  }, [dispatch]);

  useEffect(() => {
    if (isSurveySettingResponse) {
      dispatch(getSurveySetting());
      setDirty(false); // reset dirty on load
      dispatch(updatedSurveySettingResponse(false));
    }
  }, [isSurveySettingResponse]);

  useEffect(() => {
    if (surveySettingResponse) {
      if (surveySettingResponse.booking_team_emails)
        setEmails(surveySettingResponse.booking_team_emails);
      if (surveySettingResponse.start_time && surveySettingResponse.end_time) {
        const s = surveySettingResponse.start_time.slice(0, 5);
        const e = surveySettingResponse.end_time.slice(0, 5);
        setStartTime(s);
        setEndTime(e);
        setTimeSlots(generateTimeSlots(s, e));
      }
    }
  }, [surveySettingResponse]);

  // regenerate slots on time change
  useEffect(() => {
    setTimeSlots(generateTimeSlots(startTime, endTime));
  }, [startTime, endTime]);

  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const addEmail = (raw: string) => {
    setDirty(true);
    const email = raw.trim().replace(/,$/, "");
    if (!email) return;
    if (!validateEmail(email)) {
      setEmailError("Invalid email format");
      return;
    }
    if (emails.includes(email)) {
      setEmailError("Email already added");
      return;
    }
    setEmails((prev) => [...prev, email]);
    setEmailInput("");
    setEmailError("");
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail(emailInput);
    }
    if (e.key === "Backspace" && emailInput === "" && emails.length > 0) {
      setEmails((prev) => prev.slice(0, -1));
    }
  };

  const handleSave = () => {
    if (!startTime || !endTime || endTime <= startTime) {
      setDirty(false);
      dispatch(
        setNotificationAction({
          severity: "error",
          message: ["End time must be greater than start time"], // array
        }),
      );
      return;
    }
    if (emails.length === 0) {
      setEmailError("Add at least one email");
      return;
    }
    dispatch(
      updateSurveySettingAction({
        ...surveySettingResponse,
        start_time: `${startTime}:00`,
        end_time: `${endTime}:00`,
        booking_team_emails: emails,
      }),
    );
  };

  return (
    <Box sx={{ background: "#f4f4f4", minHeight: "100vh", padding: "10px" }}>
      <Box
        sx={{
          bgcolor: "#fff",
          padding: "20px",
          border: "1px solid #d1cfcf",
          borderRadius: "5px",
        }}
      >
        <PageTitle title="Survey Details" />
        {/* <Notification /> */}
        <Box sx={{ mt: 1 }}>
          {/* ── TIME RANGE ── */}
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: "text.secondary",
              letterSpacing: 0.6,
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            Time range
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <Typography
                component="label"
                sx={{ fontSize: 12, color: "text.secondary" }}
              >
                Start time
              </Typography>
              <input
                type="time"
                value={startTime}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  setDirty(true);
                }}
                style={{
                  height: 36,
                  padding: "0 10px",
                  fontSize: 13,
                  width: 180,
                  border: "1px solid #c4c4c4",
                  borderRadius: 6,
                }}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <Typography
                component="label"
                sx={{ fontSize: 12, color: "text.secondary" }}
              >
                End time
              </Typography>
              <input
                type="time"
                value={endTime}
                onChange={(e) => {
                  setEndTime(e.target.value);
                  setDirty(true);
                }}
                style={{
                  height: 36,
                  padding: "0 10px",
                  fontSize: 13,
                  width: 180,
                  border: "1px solid #c4c4c4",
                  borderRadius: 6,
                }}
              />
            </Box>
          </Box>

          {/* ── SLOTS PREVIEW ── */}
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: "text.secondary",
              letterSpacing: 0.6,
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            Generated slots
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.8,
              mb: 2,
              minHeight: 32,
            }}
          >
            {timeSlots.length > 0 ? (
              timeSlots.map((slot) => (
                <Chip
                  key={slot}
                  label={slot}
                  size="small"
                  sx={{ fontSize: 12, height: 26, bgcolor: "#f4f4f4" }}
                />
              ))
            ) : (
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Set a valid time range to preview slots
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* ── EMAIL CHIP INPUT ── */}
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: "text.secondary",
              letterSpacing: 0.6,
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            Booking team emails
          </Typography>

          <Box
            onClick={() => inputRef.current?.focus()}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.7,
              alignItems: "center",
              minHeight: 42,
              p: "6px 10px",
              border: "1px solid #c4c4c4",
              borderRadius: "6px",
              cursor: "text",
              "&:focus-within": {
                borderColor: "#02906f",
                boxShadow: "0 0 0 2px rgba(2,144,111,0.15)",
              },
            }}
          >
            {emails.map((email) => (
              <Chip
                key={email}
                label={email}
                size="small"
                onDelete={() =>
                  setEmails((prev) => prev.filter((e) => e !== email))
                }
                sx={{
                  fontSize: 14,
                  height: 26,
                  bgcolor: "#e1f5ee",
                  color: "#0f6e56",
                  "& .MuiChip-deleteIcon": { color: "#0f6e56", fontSize: 14 },
                }}
              />
            ))}
            <InputBase
              inputRef={inputRef}
              value={emailInput}
              onChange={(e) => {
                setEmailInput(e.target.value);
                setEmailError("");
              }}
              onKeyDown={handleEmailKeyDown}
              onBlur={() => emailInput && addEmail(emailInput)}
              placeholder={
                emails.length === 0 ? "Type email and press Enter" : ""
              }
              sx={{
                fontSize: 13,
                flex: 1,
                minWidth: 180,
                "& input": { padding: 0, height: 28 },
              }}
            />
          </Box>

          <Typography sx={{ fontSize: 14, fontWeight: 600, mt: 0.5 }}>
            Press Enter or comma to add · Backspace to remove last
          </Typography>
          {emailError && (
            <Typography sx={{ fontSize: 14, color: "error.main", mt: 0.3 }}>
              {emailError}
            </Typography>
          )}

          {/* ── ACTIONS ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setStartTime(
                  surveySettingResponse?.start_time?.slice(0, 5) ?? "07:00",
                );
                setEndTime(
                  surveySettingResponse?.end_time?.slice(0, 5) ?? "18:00",
                );
                setEmails(surveySettingResponse?.booking_team_emails ?? []);
                setEmailInput("");
                setEmailError("");
                setDirty(false);
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: "#02906f", "&:hover": { bgcolor: "#027a5e" } }}
              onClick={handleSave}
            >
              Save settings
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SurveySettingForm;
